const CLINICAL_ALLOWED_TAGS = new Set([
  'a',
  'br',
  'em',
  'li',
  'ol',
  'p',
  'strong',
  'u',
  'ul',
]);

const DROP_WITH_CONTENT_TAGS = new Set([
  'iframe',
  'object',
  'script',
  'style',
]);

const TAG_REPLACEMENTS: Record<string, string> = {
  b: 'strong',
  i: 'em',
};

export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const escapeHtmlAttribute = (value: string): string =>
  escapeHtml(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export const normalizeSafeHref = (href: string | null | undefined): string | null => {
  const trimmed = href?.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const baseUrl = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const parsed = new URL(trimmed, baseUrl);
    if (['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol)) {
      return trimmed;
    }
  } catch {
    return null;
  }

  return null;
};

const serializeSanitizedNode = (node: Node): string => {
  if (node.nodeType === 3) {
    return escapeHtml(node.textContent ?? '');
  }

  if (node.nodeType !== 1) {
    return '';
  }

  const element = node as Element;
  const sourceTag = element.tagName.toLowerCase();
  const tag = TAG_REPLACEMENTS[sourceTag] ?? sourceTag;

  if (DROP_WITH_CONTENT_TAGS.has(sourceTag)) {
    return '';
  }

  const children = Array.from(element.childNodes).map(serializeSanitizedNode).join('');
  if (!CLINICAL_ALLOWED_TAGS.has(tag)) {
    return children;
  }

  if (tag === 'br') {
    return '<br>';
  }

  if (tag === 'a') {
    const href = normalizeSafeHref(element.getAttribute('href'));
    if (!href) {
      return children;
    }
    return `<a href="${escapeHtmlAttribute(href)}" target="_blank" rel="noopener noreferrer">${children}</a>`;
  }

  return `<${tag}>${children}</${tag}>`;
};

const stripTagsFallback = (value: string): string => value.replace(/<[^>]*>/g, ' ');

export const sanitizeRichTextHtml = (html: string | null | undefined): string => {
  if (!html) {
    return '';
  }

  if (typeof DOMParser === 'undefined') {
    return escapeHtml(stripTagsFallback(html));
  }

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(parsed.body.childNodes).map(serializeSanitizedNode).join('');
};

export const getRichTextPlainText = (html: string | null | undefined): string => {
  if (!html) {
    return '';
  }

  const sanitized = sanitizeRichTextHtml(html);
  if (typeof DOMParser === 'undefined') {
    return stripTagsFallback(sanitized).replace(/\s+/g, ' ').trim();
  }

  const parsed = new DOMParser().parseFromString(sanitized, 'text/html');
  return (parsed.body.textContent ?? '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
};

export const isRichTextEmpty = (html: string | null | undefined): boolean =>
  getRichTextPlainText(html).length === 0;

export const normalizeRichTextHtml = (html: string | null | undefined): string | null => {
  if (isRichTextEmpty(html)) {
    return null;
  }
  return sanitizeRichTextHtml(html);
};

export const truncateRichTextPlainText = (
  html: string | null | undefined,
  maxLength: number,
): string => {
  const plainText = getRichTextPlainText(html);
  if (plainText.length <= maxLength) {
    return plainText;
  }
  return `${plainText.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
};
